import type { AvailabilityDay } from "@autopic/shared";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Parse a YYYY-MM-DD string into a local Date at midnight. */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Format YYYY-MM-DD from a Date (local). */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "Mon 29 Jun" style label. */
export function formatDayLabel(iso: string): string {
  const d = parseISODate(iso);
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]!.slice(0, 3)}`;
}

/** "Monday 29 June 2026" style label for summaries. */
export function formatLongDate(iso: string): string {
  const d = parseISODate(iso);
  const dow = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
    d.getDay()
  ];
  return `${dow} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function monthTitle(year: number, month0: number): string {
  return `${MONTHS[month0]} ${year}`;
}

export const DOW_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/** Build a Monday-first matrix of cells for a given month, annotated by availability. */
export interface CalendarCell {
  iso: string | null; // null = padding cell
  day: number | null;
  available: boolean;
  inRange: boolean;
}

export function buildMonth(
  year: number,
  month0: number,
  availByDate: Map<string, AvailabilityDay>,
): CalendarCell[] {
  const first = new Date(year, month0, 1);
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  // Monday-first offset (JS getDay: 0=Sun..6=Sat).
  const lead = (first.getDay() + 6) % 7;

  const cells: CalendarCell[] = [];
  for (let i = 0; i < lead; i++) {
    cells.push({ iso: null, day: null, available: false, inRange: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = toISODate(new Date(year, month0, d));
    const day = availByDate.get(iso);
    cells.push({
      iso,
      day: d,
      available: !!day?.hasAvailability,
      inRange: !!day,
    });
  }
  return cells;
}

/** The next date (>= a given iso) that has availability, or null. */
export function findNextAvailable(days: AvailabilityDay[], afterIso?: string): AvailabilityDay | null {
  for (const d of days) {
    if (afterIso && d.date <= afterIso) continue;
    if (d.hasAvailability) return d;
  }
  return null;
}
